import { createContext, useContext, useState, useCallback, useRef, useMemo } from 'react'
import type { Shortcut, ShortcutContextValue } from '@/types/shortcuts'

const ShortcutContext = createContext<ShortcutContextValue | null>(null)

export function useShortcutContext(): ShortcutContextValue {
  const ctx = useContext(ShortcutContext)
  if (!ctx) throw new Error('useShortcutContext must be used within ShortcutProvider')
  return ctx
}

export function ShortcutProvider({ children }: { children: React.ReactNode }) {
  const registryRef = useRef<Map<string, Shortcut[]>>(new Map())
  const [shortcuts, setShortcuts] = useState<Shortcut[]>([])
  const [showHelp, setShowHelp] = useState(false)

  const rebuildShortcuts = useCallback(() => {
    const all: Shortcut[] = []
    registryRef.current.forEach((list) => all.push(...list))
    setShortcuts(prev => {
      if (prev.length === all.length && prev.every((s, i) => s === all[i])) return prev
      return all
    })
  }, [])

  const registerShortcuts = useCallback((id: string, list: Shortcut[]) => {
    registryRef.current.set(id, list)
    rebuildShortcuts()
  }, [rebuildShortcuts])

  const unregisterShortcuts = useCallback((id: string) => {
    registryRef.current.delete(id)
    rebuildShortcuts()
  }, [rebuildShortcuts])

  const toggleHelp = useCallback(() => setShowHelp((prev) => !prev), [])

  const value = useMemo(() => ({
    shortcuts,
    registerShortcuts,
    unregisterShortcuts,
    showHelp,
    toggleHelp,
  }), [shortcuts, registerShortcuts, unregisterShortcuts, showHelp, toggleHelp])

  return (
    <ShortcutContext.Provider value={value}>
      {children}
    </ShortcutContext.Provider>
  )
}
